/** Canonical BS categories for manual line items and SOX grouping (no separate "other" section). */
export const BALANCE_SHEET_LINE_CATEGORIES = ['asset', 'liability', 'equity'];

/** Canonical IS categories for manual line items (revenue vs expense). */
export const INCOME_STATEMENT_LINE_CATEGORIES = ['revenue', 'expense'];

/**
 * Maps stored/API category to revenue | expense for UI grouping.
 * Unknown/missing category defaults to revenue (same idea as BS unknown → asset).
 */
export function normalizeIncomeStatementItemCategory(item) {
  const c = String(item?.category ?? '')
    .toLowerCase()
    .trim();
  if (c === 'expense' || c === 'expenses') return 'expense';
  if (c === 'revenue' || c === 'revenues') return 'revenue';
  return 'revenue';
}

/**
 * Splits income statement line items for SOX mapping UI by revenue vs expense.
 * Single pass O(n); each row includes flatIdx (index in `items`) for stable SOX keys / notes — no indexOf.
 *
 * @returns {{ revenueRows: Array<{ item: unknown, flatIdx: number }>, expenseRows: Array<{ item: unknown, flatIdx: number }> }}
 */
export function partitionIncomeStatementItemsForSoxMapping(items) {
  if (!Array.isArray(items)) {
    return { revenueRows: [], expenseRows: [] };
  }
  const revenueRows = [];
  const expenseRows = [];
  for (let flatIdx = 0; flatIdx < items.length; flatIdx++) {
    const item = items[flatIdx];
    const row = { item, flatIdx };
    if (normalizeIncomeStatementItemCategory(item) === 'revenue') {
      revenueRows.push(row);
    } else {
      expenseRows.push(row);
    }
  }
  return { revenueRows, expenseRows };
}

/**
 * Maps stored/API category to asset | liability | equity for UI grouping.
 * Values outside those three are folded into asset so every line appears under one heading.
 */
export function normalizeBalanceSheetItemCategory(item) {
  const c = String(item?.category ?? '')
    .toLowerCase()
    .trim();
  if (c === 'asset' || c === 'assets') return 'asset';
  if (c === 'liability' || c === 'liabilities') return 'liability';
  if (c === 'equity' || c === 'equities') return 'equity';
  return 'asset';
}

/** Prefix used in SOX item ids / mappings for balance sheet rows (no bs_other). */
export function balanceSheetSoxItemTypePrefix(item) {
  const n = normalizeBalanceSheetItemCategory(item);
  if (n === 'liability') return 'bs_liability';
  if (n === 'equity') return 'bs_equity';
  return 'bs_asset';
}
